import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { sessionChannel } from '@/shared/auth/sessionChannel';
import type { SessionTokens } from '@/shared/auth/sessionChannel';
import { forgetSessionScopedQueries } from '@/shared/api/sessionScope';
import { tokenStorage } from '@/shared/auth/tokenStorage';

import { ApiError } from '@/shared/api/ApiError';

import { AuthContext } from './AuthContext';
import type { AuthContextValue, SessionStatus } from './AuthContext';

import { revokeSession } from '../api/currentUser';
import { signIn as exchangeCredentials } from '../api/signIn';
import { useCurrentUser } from '../hooks/useCurrentUser';

/**
 * Who is signed in, in one place.
 *
 * <h2>Why this exists rather than each screen reading storage</h2>
 *
 * Without it, "am I signed in" is answered differently in every component that
 * asks — one reads `tokenStorage`, another fetches the user, a third assumes. They
 * drift, and the drift shows up as a screen that renders for somebody it should
 * not, or a sign-out that leaves a stale name in a header.
 *
 * <p>It also means signing out is one implementation. A component that clears
 * storage and navigates has skipped revocation, and there is nothing to stop the
 * next one doing the same.
 *
 * <h2>The user is fetched, never derived from the token</h2>
 *
 * Access tokens here are opaque references, so nothing about a person can be read
 * out of one in the browser. That is a property worth keeping: a client that
 * decoded identity from a token would be trusting a value it cannot verify.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  /**
   * Whether a token exists, in state rather than read on every render.
   *
   * <p>Reading storage during render looks equivalent and is not: writing a token
   * does not re-render anything, so a screen that signed in would go on believing
   * nobody was, until something unrelated happened to re-render this provider.
   * Holding it in state means signing in and out are the two things that change
   * it, and both go through here.
   */
  const [hasToken, setHasToken] = useState(() => Boolean(tokenStorage.accessToken()));
  const { data, isError, error, refetch } = useCurrentUser({ enabled: hasToken });
  const queryClient = useQueryClient();

  /**
   * Ends the session in this tab: the tokens, the cache, and the flag the app reads.
   *
   * <p>Shared by signing out and by hearing that another tab did, so the two cannot drift
   * into clearing different things — and the cache is the one that gets forgotten, which is
   * how one account's rows stay on screen under another account's name.
   */
  const forgetSession = useCallback(() => {
    tokenStorage.clear();
    forgetSessionScopedQueries(queryClient);
    setHasToken(false);
  }, [queryClient]);

  /**
   * Takes up the session that is already in storage, without having signed in here.
   *
   * <p>For the tab that hears another one sign in. The cache is dropped first because
   * whatever is in it was fetched as nobody, or as somebody else.
   *
   * <h2>It writes what it was told rather than reading what it hopes is there</h2>
   *
   * This used to be `setHasToken(Boolean(tokenStorage.accessToken()))` — hear the
   * announcement, then go and read storage. Those are two transports with no ordering
   * between them, and across renderer processes the message can arrive first: the read
   * returned `null`, this tab decided there was no session, and stayed on the sign-in form
   * with no second chance, because nothing re-reads storage and the announcement does not
   * repeat. Measured at 2 misses in 150 before the change.
   *
   * <p>Writing the session here is not redundant with the sender having written it. It is
   * what makes it visible to THIS tab immediately — the API calls that follow the redirect
   * read the same storage, so adopting the session without writing it would move the tab to
   * the dashboard and then bounce it back on the first 401.
   *
   * <p>`session` is null only for an announcement from a tab running older code. Then there
   * is nothing to write and reading storage is the best available answer, which is the
   * behaviour this replaced.
   */
  const adoptSession = useCallback(
    (session: SessionTokens | null) => {
      forgetSessionScopedQueries(queryClient);

      if (session) {
        tokenStorage.start(session);
        setHasToken(true);
        return;
      }

      setHasToken(Boolean(tokenStorage.accessToken()));
    },
    [queryClient],
  );

  /**
   * Exchanges credentials for a session.
   *
   * <p>On the context rather than left to each screen, so there is one definition
   * of what signing in does: store the token, drop everything cached as whoever
   * was here before, and tell the application a session exists. A screen that did
   * its own would be a second implementation of all three, and the one it forgot
   * would be the cache.
   */
  const signIn = useCallback(
    async (email: string, password: string) => {
      const session = await exchangeCredentials({ email, password });

      // start, not save: start clears whatever was held before, save keeps a
      // session where it is and is for rotation.
      tokenStorage.start({
        accessToken: session.accessToken,
        refreshToken: session.refreshToken,
      });

      // Whatever is cached was fetched as somebody else, or as nobody. Signing in
      // over a warm cache is how one account briefly sees another's data on a
      // shared machine.
      forgetSessionScopedQueries(queryClient);
      setHasToken(true);

      // The other tabs are sitting on a sign-in form with no reason to look at storage
      // again. Told, each takes up the session and GuestRoute moves it to the dashboard.
      // Carrying the session, not just the fact of one: a tab told only that somebody
      // signed in has to go and read storage, and may get there before the write is
      // visible to it. See sessionChannel.
      sessionChannel.announceSignIn({
        accessToken: session.accessToken,
        refreshToken: session.refreshToken,
      });
    },
    [queryClient],
  );

  /**
   * Forgets the session locally, and drops it everywhere else it exists.
   *
   * <p>Three places, and each is needed for a different reason. `revokeSession` kills the
   * token on the SERVER, which is the only half that reaches a copy someone took off this
   * machine — and the only half that reaches another tab's `sessionStorage`, which this tab
   * cannot read. `tokenStorage.clear` and `queryClient.clear` end it here. The announcement
   * tells the other tabs to do their own, immediately, instead of discovering it when their
   * next request fails.
   */
  const signOut = useCallback(async () => {
    try {
      await revokeSession();
    } catch {
      // Deliberately swallowed. The server may be unreachable, or the token may
      // already be dead -- neither is a reason to leave somebody signed in on
      // this machine. The local half must always happen.
    } finally {
      forgetSession();

      // After the local half, not before: a tab that heard this and cleared itself while
      // this one still held a token would be the only one signed out.
      sessionChannel.announceSignOut();
    }
  }, [forgetSession]);

  /*
   * ANOTHER TAB SIGNED IN OR OUT, so this one follows.
   *
   * Storage cannot carry this on its own -- `sessionStorage` is per tab, so the copy in
   * this tab is invisible to the one that signed out, and `localStorage` is shared but
   * silent, leaving a signed-in-looking screen until the next request fails. Each tab
   * clears its own, on the word of the tab that did it.
   *
   * <p>Nothing navigates here, in either direction. Changing the session is enough:
   * `ProtectedRoute` sends a tab to the sign-in screen the moment there is no user, and
   * `GuestRoute` sends one to the dashboard the moment there is — the same paths a session
   * expiring and a sign-in already take.
   */
  useEffect(
    () => sessionChannel.onSessionChange({ signedIn: adoptSession, signedOut: forgetSession }),
    [adoptSession, forgetSession],
  );

  /*
   * THE SERVER REFUSED THE TOKEN, so this browser is holding a dead one.
   *
   * Left in storage it is worse than nothing: it is the difference between the two
   * questions the guards used to ask, and the state where a browser had the dashboard and
   * the sign-in form both available to it. Forgetting it makes every guard agree, and makes
   * the next reload cheap -- no token, no request, no wait.
   *
   * <p>Only on 401 and 403. A 500 or an unreachable server says nothing about the session,
   * and signing somebody out over a backend restart would be a bug of our own making. The
   * query's own retries cover the brief case; what reaches here has failed repeatedly.
   */
  const authRefused =
    isError && error instanceof ApiError && (error.status === 401 || error.status === 403);

  useEffect(() => {
    if (authRefused) {
      forgetSession();
    }
  }, [authRefused, forgetSession]);

  /*
   * ONE DEFINITION, and the three guards read it rather than each deciding.
   *
   * Order matters. No token is the only case that needs no server at all. After that the
   * server's answer is what counts: data means signed in, a refusal means signed out, and
   * anything else -- in flight, retrying -- is not yet known and must not be guessed.
   */
  const status: SessionStatus = !hasToken
    ? 'signed-out'
    : data
      ? 'signed-in'
      : authRefused
        ? 'signed-out'
        : 'unknown';

  /*
   * THE SERVER COULD NOT BE ASKED, which is not the same as being told no.
   *
   * This branch read `isError` and answered 'signed-out' for ANY failure, which contradicted
   * the rule four lines above it: the token is only forgotten on 401 and 403, deliberately,
   * because a 500 or an unreachable backend says nothing about the session.
   *
   * The two disagreeing is an infinite redirect. A token in storage plus a connection
   * refused gave `hasStoredSession = true` and `status = 'signed-out'`, so ProtectedRoute
   * sent the browser to /login and GuestRoute sent it straight back to /dashboard, for ever.
   * It is exactly the two-definitions bug the status field was introduced to end, arriving
   * through the one case the field itself got wrong -- and the only thing needed to trigger
   * it is the backend being down while somebody has the app open.
   *
   * So an unanswerable session is `unknown`. The guards wait rather than guess, and this
   * says WHY they are waiting, because a spinner that never resolves is its own dead end.
   */
  const sessionUnreachable = hasToken && isError && !authRefused;

  const value = useMemo<AuthContextValue>(
    () => ({
      user: data ?? null,
      status,
      hasStoredSession: hasToken,
      isLoading: status === 'unknown',
      sessionUnreachable,
      retrySession: () => {
        void refetch();
      },
      signIn,
      signOut,
    }),
    [data, status, hasToken, sessionUnreachable, refetch, signIn, signOut],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}
